import { clamp } from '@iconik-io/core/src/clamp';
import { roundToDecimals } from '@iconik-io/core/src/round_to_decimals';

import { MAX_ZOOM, MIN_ZOOM } from '../constants.js';
import type { Point, Size } from '../types.js';

export function getRotatedDimensions(
  width: number,
  height: number,
  rotation: number,
): Size {
  const angleRad = (rotation * Math.PI) / 180;
  const cos = Math.abs(Math.cos(angleRad));
  const sin = Math.abs(Math.sin(angleRad));

  const rotatedWidth = width * cos + height * sin;
  const rotatedHeight = width * sin + height * cos;

  return { width: rotatedWidth, height: rotatedHeight };
}

/**
 * The unrotated size the content is rendered at so that its rotated bounding box
 * still fits the container. The unrotated box has to fit the container too, because
 * the media element is laid out before it is rotated and is capped at the container
 * size by `--content-max-width` and `max-height`
 */
export function getRotationFittedSize(
  contentSize: Size,
  containerSize: Size,
  rotation: number,
): Size {
  const rotated = getRotatedDimensions(
    contentSize.width,
    contentSize.height,
    rotation,
  );

  const scale = Math.min(
    containerSize.width / rotated.width,
    containerSize.height / rotated.height,
    containerSize.width / contentSize.width,
    containerSize.height / contentSize.height,
  );

  if (!Number.isFinite(scale) || scale <= 0) {
    return contentSize;
  }

  return {
    width: contentSize.width * scale,
    height: contentSize.height * scale,
  };
}

/**
 * How much of the content is visible on each of its own axes, so a value of
 * `{ x: 0.5, y: 1 }` means the left or right half of the content fills the container
 */
export function getVisibleContentFraction(
  contentSize: Size,
  containerSize: Size,
  zoom: number,
  rotation: number,
): Point {
  const fittedSize = getRotationFittedSize(
    contentSize,
    containerSize,
    rotation,
  );
  const containerOnContentAxes = getRotatedDimensions(
    containerSize.width,
    containerSize.height,
    rotation,
  );

  const zoomedWidth = fittedSize.width * zoom;
  const zoomedHeight = fittedSize.height * zoom;

  return {
    x:
      zoomedWidth > 0
        ? clamp(containerOnContentAxes.width / zoomedWidth, 0, 1)
        : 1,
    y:
      zoomedHeight > 0
        ? clamp(containerOnContentAxes.height / zoomedHeight, 0, 1)
        : 1,
  };
}

/**
 * `imageWidth` and `imageHeight` are the size the content is displayed at, which for
 * a rotated view is the size from `getRotationFittedSize`
 */
export function calculateTransformations(
  zoom: number,
  pan: Point,
  imageWidth: number,
  imageHeight: number,
  containerWidth: number,
  containerHeight: number,
  rotation: number,
) {
  const { width: rotatedWidth, height: rotatedHeight } = getRotatedDimensions(
    imageWidth * zoom,
    imageHeight * zoom,
    rotation,
  );

  const angleRad = (rotation * Math.PI) / 180;

  const panX = pan.x - 0.5;
  const panY = pan.y - 0.5;

  const rotatedPanX = panX * Math.cos(angleRad) - panY * Math.sin(angleRad);
  const rotatedPanY = panX * Math.sin(angleRad) + panY * Math.cos(angleRad);

  const offsetX = -rotatedPanX * Math.max(0, rotatedWidth - containerWidth);
  const offsetY = -rotatedPanY * Math.max(0, rotatedHeight - containerHeight);

  return { offsetX, offsetY, zoom };
}

export function calculatePanFromCenterPoint(
  zoom: number,
  centerX: number,
  centerY: number,
) {
  if (zoom <= 1) {
    // Zoom 1 cause division by 0 further down, and for any value below 1 we want the pan to be 0.5 anyways
    return { x: 0.5, y: 0.5 };
  }

  // Calculate valid area dimensions
  const validWidth = 1 / zoom;
  const validHeight = 1 / zoom;

  // Calculate top-left corner after panning to center the desired point
  const topLeftX = centerX - validWidth / 2;
  const topLeftY = centerY - validHeight / 2;

  // Calculate the pan (clamping values to the 0-1 range)
  const panX = clamp(topLeftX / (1 - validWidth), 0, 1);
  const panY = clamp(topLeftY / (1 - validHeight), 0, 1);

  return { x: panX, y: panY };
}

function calculateZoomPanForAxis(
  currentPanAxis: number,
  currentZoom: number,
  newZoom: number,
  focusPointAxis: number,
) {
  if (newZoom <= 1.05) {
    return 0.5;
  }

  const currentStart = currentPanAxis * (1 - 1 / currentZoom);
  const currentWidth = 1 / currentZoom;
  const relativePosition = (focusPointAxis - currentStart) / currentWidth;

  const newWidth = 1 / newZoom;
  const newStart = focusPointAxis - relativePosition * newWidth;
  const newPanAxis = newStart / (1 - newWidth);

  // Smoothen the pan for zoom levels below 2 due to the large swings `/ (1 - newWidth)` gives when newWidth is close to 1
  const dampingFactor = newZoom > 2 ? 1 : (newZoom - 1) ** 0.9;
  const smoothedPanAxis =
    currentPanAxis + (newPanAxis - currentPanAxis) * dampingFactor;

  return smoothedPanAxis;
}

export function calculateZoomPan(
  currentPan: Point,
  currentZoom: number,
  newZoom: number,
  focusPoint: Point,
) {
  return {
    x: calculateZoomPanForAxis(
      currentPan.x,
      currentZoom,
      newZoom,
      focusPoint.x,
    ),
    y: calculateZoomPanForAxis(
      currentPan.y,
      currentZoom,
      newZoom,
      focusPoint.y,
    ),
  };
}

const getOverlayRelativePoint = (point: Point, containerSize: Size) => {
  return {
    x: point.x / containerSize.width,
    y: point.y / containerSize.height,
  };
};

function getZoomPanAdjustedPoint(
  containerSize: Size,
  contentSize: Size,
  point: Point,
  zoom: number,
  pan: Point,
  rotation: number,
): Point {
  const resizedContentSize = {
    width: zoom * contentSize.width,
    height: zoom * contentSize.height,
  };

  // Rotate pan
  const { width: rotatedWidth, height: rotatedHeight } = getRotatedDimensions(
    resizedContentSize.width,
    resizedContentSize.height,
    rotation,
  );

  // Calculate the offsets based on pan values
  const offsetX = (containerSize.width - rotatedWidth) * pan.x;
  const offsetY = (containerSize.height - rotatedHeight) * pan.y;

  // Convert normalized coordinates (0 to 1) of the container to pixel coordinates
  const xPixel = point.x * containerSize.width;
  const yPixel = point.y * containerSize.height;

  // Adjust coordinates to the content, considering the offset, scale, and rotation
  return {
    x:
      rotatedWidth < containerSize.width
        ? 0.5
        : clamp((xPixel - offsetX) / rotatedWidth, 0, 1),
    y:
      rotatedHeight < containerSize.height
        ? 0.5
        : clamp((yPixel - offsetY) / rotatedHeight, 0, 1),
  };
}

export function getRelativePoint(
  point: Point,
  containerSize: Size,
  contentSize: Size,
  zoom: number,
  pan: Point,
  rotation: number,
): Point {
  const relativePoint = getOverlayRelativePoint(point, containerSize);
  const resizedContentSize = getRotationFittedSize(
    contentSize,
    containerSize,
    rotation,
  );
  const transformedPoint = getZoomPanAdjustedPoint(
    containerSize,
    resizedContentSize,
    relativePoint,
    zoom,
    pan,
    rotation,
  );

  return transformedPoint;
}

export function clampZoom(zoom: number) {
  return clamp(roundToDecimals(zoom, 4), MIN_ZOOM, MAX_ZOOM);
}
